import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, X, Mail, Check, DollarSign, Users } from 'lucide-react';

// Hero photo. Unsplash License, by Age Cymru — warm candid portrait of a
// smiling older man that matches the "earn from wisdom" pitch without looking
// stocky. Wrapped in a dark warm gradient to guarantee form legibility.
const HERO_IMG = 'https://images.unsplash.com/photo-1750853736853-7eed0c15d4a0?w=1600&q=75&fit=crop';

const Auth = () => {
  const { login, signup } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Ads bring cold traffic — default to signup, existing users toggle or use ?login=1
  const initialMode = searchParams.get('login') === '1' ? 'login' : 'signup';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);

  // Forgot-password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('forgot') === '1') {
      setForgotOpen(true);
      searchParams.delete('forgot');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Defence-in-depth: even if this component ever renders under a wrapper
  // that has locked scrolling (e.g. `.App.app-mode` or `body.no-scroll`),
  // force scroll back on while Auth is mounted. Restore on unmount so the
  // rest of the app keeps its normal locked-in-viewport behaviour.
  useEffect(() => {
    const hadNoScroll = document.body.classList.contains('no-scroll');
    document.body.classList.remove('no-scroll');
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      if (hadNoScroll) document.body.classList.add('no-scroll');
    };
  }, []);

  useEffect(() => {
    api('/api/waitlist/count')
      .then((d) => { if (d?.success) setWaitlistCount(d.count); })
      .catch(() => {});
  }, []);

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await api('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    }
    setForgotLoading(false);
  };

  const closeForgot = () => { setForgotOpen(false); setForgotSent(false); setForgotEmail(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        if (!fullName.trim()) { setError('Name is required'); setLoading(false); return; }
        await signup(email, password, fullName);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    }
    setLoading(false);
  };

  // ==========================================================================
  // Root layout notes (fixes the mobile scroll bug reported on Safari / Edge /
  // Chrome mobile):
  //   * NO `overflow-hidden` on the root — that was clipping the form.
  //   * `min-h-[100dvh]` uses the *dynamic* viewport height so the URL bar
  //     eating pixels on mobile Safari doesn't push the form off-screen.
  //     Fallback `min-h-screen` covers browsers without dvh support.
  //   * `md:justify-center` (not plain `justify-center`) — vertical centering
  //     is only applied on desktop where the viewport is tall enough. On
  //     mobile the content flows top-down and the page scrolls naturally.
  //   * Background image is `fixed`, so it stays as a backdrop while form
  //     scrolls independently.
  // ==========================================================================
  return (
    <div
      className="isolate relative w-full min-h-screen min-h-[100dvh] font-body auth-root"
      data-testid="auth-page"
    >
      {/* Background hero photo — FIXED so it doesn't push layout height */}
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-mate-terracotta/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      </div>

      {/* Content column — natural flow so overflow scrolls the whole page */}
      <div className="relative flex flex-col min-h-screen min-h-[100dvh] px-5 py-8 md:py-12">
        {/* Top bar: logo + Australian pill + always-visible Sign in link */}
        <div className="flex items-center justify-between max-w-5xl w-full mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-mate-green flex items-center justify-center shadow-lg">
              <span className="text-white font-heading font-bold text-xl">6</span>
            </div>
            <span className="font-heading font-bold text-xl text-white tracking-tight">6th Mate</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
              <span className="w-1.5 h-1.5 rounded-full bg-mate-terracotta animate-pulse" />
              Australian-based
            </div>
            {/* Always-visible mode toggle — critical: on short mobile viewports
                the toggle at the bottom of the form card can sit below the
                fold. This one is anchored at the top so users can always
                switch between Sign in / Create account without scrolling. */}
            <button
              type="button"
              onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setError(''); }}
              className="text-white text-sm font-semibold underline underline-offset-4 decoration-white/50 hover:decoration-white transition"
              data-testid="auth-topnav-toggle"
            >
              {mode === 'signup' ? 'Sign in' : 'Sign up'}
            </button>
          </div>
        </div>

        {/* Middle: hook + form. `md:justify-center` centers on desktop only.
            On mobile it flows top-down so the whole page can scroll. */}
        <div className="flex-1 flex flex-col md:justify-center max-w-5xl w-full mx-auto mt-8 md:mt-0">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 w-full md:items-center">

            {/* Hook side */}
            <div className="text-white">
              <h1
                className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4 md:mb-5 drop-shadow-lg"
                style={{ letterSpacing: '-0.02em' }}
                data-testid="auth-hook-headline"
              >
                Earn money sharing{' '}
                <span className="text-mate-terracotta">your life experience</span>
              </h1>
              <p className="text-base md:text-lg text-white/90 leading-relaxed mb-5 md:mb-6 max-w-md drop-shadow">
                Relaxed video calls from your favourite chair. No commute, no boss, no formal qualifications needed.
              </p>
              <div className="flex flex-wrap gap-2 md:gap-3">
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full border border-white/20" data-testid="proof-earn">
                  <DollarSign size={14} className="text-mate-terracotta" /> Choose your own rates
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full border border-white/20" data-testid="proof-noquals">
                  <Check size={14} className="text-mate-green" /> No qualifications needed
                </span>
                {waitlistCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md text-white text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full border border-white/20" data-testid="proof-waitlist">
                    <Users size={14} className="text-white" /> {waitlistCount.toLocaleString('en-AU')}+ already signed up
                  </span>
                )}
              </div>
            </div>

            {/* Form side */}
            <div className="w-full max-w-sm md:max-w-md md:ml-auto">
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 md:p-7 shadow-2xl border border-white/40">
                <h2 className="font-heading font-bold text-2xl text-gray-900 mb-1" data-testid="auth-title">
                  {mode === 'signup' ? 'Create your free account' : 'Welcome back'}
                </h2>
                <p className="text-gray-500 text-sm mb-5">
                  {mode === 'signup' ? 'Takes under a minute. Cancel anytime.' : 'Sign in to continue'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-3.5">
                  {mode === 'signup' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-mate-mist bg-mate-sand/40 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-mate-green/30 focus:border-mate-green transition"
                        placeholder="e.g. Margaret Wilson"
                        data-testid="auth-name-input"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-mate-mist bg-mate-sand/40 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-mate-green/30 focus:border-mate-green transition"
                      placeholder="you@email.com"
                      data-testid="auth-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        required
                        autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-mate-mist bg-mate-sand/40 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-mate-green/30 focus:border-mate-green transition pr-11"
                        placeholder="Min 6 characters"
                        data-testid="auth-password-input"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label={showPw ? 'Hide password' : 'Show password'}>
                        {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {mode === 'login' && (
                    <div className="text-right -mt-1">
                      <button
                        type="button"
                        onClick={() => setForgotOpen(true)}
                        className="text-xs text-mate-terracotta hover:underline font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  {error && <p className="text-red-500 text-sm text-center" data-testid="auth-error">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-mate-terracotta text-white font-semibold py-3.5 rounded-xl hover:scale-[1.02] active:scale-100 transition-transform shadow-lg shadow-mate-terracotta/30 disabled:opacity-60 disabled:hover:scale-100"
                    data-testid="auth-submit-btn"
                  >
                    {loading ? 'Please wait…' : mode === 'signup' ? 'Create my account' : 'Sign In'}
                    {!loading && <ArrowRight size={18} />}
                  </button>
                </form>

                <p className="text-center text-sm text-gray-600 mt-5">
                  {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <button
                    onClick={() => { setMode(mode === 'signup' ? 'login' : 'signup'); setError(''); }}
                    className="text-mate-terracotta font-semibold hover:underline"
                    data-testid="auth-toggle-btn"
                  >
                    {mode === 'signup' ? 'Sign in' : 'Sign up free'}
                  </button>
                </p>
              </div>

              <p className="text-center text-xs text-white/70 mt-4 px-2 drop-shadow" data-testid="auth-reassure">
                Free to join · We&rsquo;ll never share your email · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot password modal */}
      {forgotOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" data-testid="forgot-password-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeForgot} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="forgot-close">
              <X size={20} />
            </button>
            {!forgotSent ? (
              <>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-1">Reset your password</h2>
                <p className="text-sm text-gray-500 mb-4">Enter the email tied to your account. We&rsquo;ll send you a one-time link valid for 1 hour.</p>
                <form onSubmit={submitForgot} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-mate-mist bg-mate-sand/50 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-mate-green/30 focus:border-mate-green transition"
                    placeholder="you@email.com"
                    data-testid="forgot-email-input"
                  />
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 bg-mate-terracotta text-white font-semibold py-3 rounded-xl hover:scale-[1.02] transition-transform disabled:opacity-60"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-2" data-testid="forgot-sent-confirmation">
                <div className="mx-auto w-12 h-12 rounded-full bg-mate-green/10 flex items-center justify-center mb-3">
                  <Mail size={22} className="text-mate-green" />
                </div>
                <h2 className="font-heading font-bold text-xl text-gray-900 mb-1">Check your inbox</h2>
                <p className="text-sm text-gray-600 mb-4">
                  If an account exists for <strong>{forgotEmail}</strong>, we&rsquo;ve just sent a reset link. It expires in 1 hour.
                </p>
                <button
                  onClick={closeForgot}
                  className="w-full bg-mate-green text-white font-semibold py-3 rounded-xl hover:scale-[1.02] transition-transform"
                  data-testid="forgot-done-btn"
                >
                  Got it
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
